import { Component, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { SiteStateService } from '@states/site-state.service';
import { BillService } from '@api/bill.service';
import { BillUploadQueueService, BillUploadJob } from '@states/bill-upload-queue.service';

// header dot + failure banner: one snapshot per modal open
export interface OcrHealthView {
  level: 'ok' | 'degraded' | 'down';
  label: string;
}

@Component({
  selector: 'app-modal-bill-upload',
  templateUrl: './modal-bill-upload.component.html',
  styleUrls: ['./modal-bill-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalBillUploadComponent {
  // native file input, so we can clear it after enqueuing
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  selectedFiles: File[] = [];

  // selected site shown for context, and its id sent with each upload
  selectedSiteName = '';
  selectedSiteId: number | null = null;

  // live queue rendered by the template (state lives in the singleton service,
  // so it survives this modal being closed and reopened)
  readonly jobs$: Observable<BillUploadJob[]>;

  // OCR health snapshot, fetched once when the modal opens (shareReplay lets the
  // header dot and the banner share the single request)
  readonly health$: Observable<OcrHealthView>;

  constructor(
    public dialogRef: MatDialogRef<ModalBillUploadComponent>,
    private siteStateService: SiteStateService,
    private queue: BillUploadQueueService,
    private billService: BillService
  ) {
    const site = this.siteStateService.selectedSiteSource.getValue();
    this.selectedSiteName = site?.name || 'No site selected';
    this.selectedSiteId = site?.siteId ?? null;

    // site-scoped: only this site's upload history, never other sites'
    this.jobs$ = this.queue.jobsForSite$(this.selectedSiteId ?? 0);
    this.health$ = this.billService.health$().pipe(
      map((h: any): OcrHealthView => {
        if (!h?.reachable) {
          return { level: 'down', label: 'OCR Service Offline' };
        }
        if (h?.ocr?.status !== 'ok') {
          return { level: 'degraded', label: 'OCR Service Degraded' };
        }
        return { level: 'ok', label: 'OCR Service Online' };
      }),
      shareReplay(1)
    );
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFiles = input.files ? Array.from(input.files) : [];
  }

  upload(): void {
    if (this.selectedFiles.length === 0) return;
    this.queue.enqueue(this.selectedFiles, this.selectedSiteId ?? 0);
    this.selectedFiles = [];
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  clearFinished(): void {
    this.queue.clearFinished(this.selectedSiteId ?? 0);
  }

  confirmOverwrite(id: number): void {
    this.queue.confirmOverwrite(id);
  }

  cancelOverwrite(id: number): void {
    this.queue.cancel(id);
  }

  statusLabel(job: BillUploadJob): string {
    switch (job.status) {
      case 'queued': return 'Queued';
      case 'processing': return 'Processing…';
      case 'needsConfirmation': return 'Needs confirmation';
      case 'done': return job.anyWritten ? 'Saved to readings' : 'Done — nothing written';
      case 'error': return 'Failed';
      case 'cancelled': return 'Cancelled';
      default: return '';
    }
  }

  trackByJobId(_index: number, job: BillUploadJob): number {
    return job.id;
  }

  close(): void {
    this.dialogRef.close();
  }
}
